import React, { useState } from 'react';
import Icon from '../AppIcon';

/**
 * Reusable FAQ Component with Accordion
 * 
 * Features:
 * - Expand/collapse accordion functionality
 * - SEO-friendly HTML (no hidden content tricks)
 * - Accessibility (ARIA roles, keyboard navigation)
 * - Smooth animations
 * 
 * @param {Array} faqs - Array of FAQ objects with question and answer
 * @param {String} title - Section title (optional)
 * @param {String} description - Section description (optional)
 * @param {String} className - Additional CSS classes (optional)
 */
const FAQ = ({ 
  faqs = [], 
  title = "Frequently Asked Questions",
  description = "Get answers to common questions",
  className = "",
  defaultExpanded = null // Index of FAQ to expand by default
}) => {
  const [expandedIndex, setExpandedIndex] = useState(defaultExpanded);

  const toggleFAQ = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const handleKeyDown = (event, index) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleFAQ(index);
    }
  };

  if (!faqs || faqs.length === 0) {
    return null;
  }

  return (
    <section 
      id="faq" 
      className={`py-12 md:py-20 bg-gray-50 ${className}`}
      aria-label="Frequently Asked Questions"
    >
      <div className="container mx-auto px-4 md:px-6">
        {(title || description) && (
          <div className="text-center mb-8 md:mb-16">
            {title && (
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">
                {title}
              </h2>
            )}
            {title && <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>}
            {description && (
              <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
                {description}
              </p>
            )}
          </div>
        )}

        <div className="max-w-4xl mx-auto">
          <div className="space-y-4" role="list">
            {faqs.map((faq, index) => {
              const isExpanded = expandedIndex === index;
              
              return (
                <div 
                  key={index}
                  className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden"
                  role="listitem"
                >
                  <button
                    className="w-full p-6 text-left focus:outline-none focus:ring-2 focus:ring-[#00d690] focus:ring-opacity-50 focus:ring-offset-2 rounded-full btn-animate-pulse"
                    onClick={() => toggleFAQ(index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    aria-expanded={isExpanded}
                    aria-controls={`faq-answer-${index}`}
                    id={`faq-question-${index}`}
                    type="button"
                  >
                    <div className="flex justify-between items-start gap-4">
                      <h3 
                        className="text-lg font-semibold text-gray-800 pr-4 leading-tight flex-1"
                        id={`faq-question-heading-${index}`}
                      >
                        {faq.question}
                      </h3>
                      <div className="flex-shrink-0">
                        <Icon
                          name={isExpanded ? "ChevronUp" : "ChevronDown"}
                          size={20}
                          className="text-[#00d690] transition-transform duration-300"
                          aria-hidden="true"
                        />
                      </div>
                    </div>
                  </button>

                  <div
                    id={`faq-answer-${index}`}
                    role="region"
                    aria-labelledby={`faq-question-heading-${index}`}
                    className={`transition-all duration-300 ease-in-out ${
                      isExpanded
                        ? 'max-h-[500px] opacity-100'
                        : 'max-h-0 opacity-0 overflow-hidden'
                    }`}
                  >
                    <div className="px-6 pb-6">
                      <div className="border-t border-gray-100 pt-4">
                        <p className="text-gray-600 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;

